// ============================================================
//  src/admin/AdminRouter.js
// ============================================================
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import AdminLayout from './components/AdminLayout';
import { ADMIN_BASE } from './adminConfig';

// -- Lazy-load admin pages ----------------------------------
const AdminLoginPage              = lazy(() => import('./pages/login/AdminLoginPage'));
const AdminDashboardPage          = lazy(() => import('./pages/dashboard/AdminDashboardPage'));
const AdminSubscribersPage        = lazy(() => import('./pages/subscribers/AdminSubscribersPage'));
const AdminSubscriberDetailsPage  = lazy(() => import('./pages/subscribers/AdminSubscriberDetailsPage'));
const AdminTransactionsPage       = lazy(() => import('./pages/transactions/AdminTransactionsPage'));
const AdminTransactionDetailsPage = lazy(() => import('./pages/transactions/AdminTransactionDetailsPage'));
const AdminSupportPage            = lazy(() => import('./pages/support/AdminSupportPage'));
const AdminSettingsPage           = lazy(() => import('./pages/settings/AdminSettingsPage'));
const AdminSystemUsersPage        = lazy(() => import('./pages/system-users/AdminSystemUsersPage'));
const AdminAccountPage            = lazy(() => import('./pages/account/AdminAccountPage'));
const AdminCreditDebitPage        = lazy(() => import('./pages/credit-debit/AdminCreditDebitPage'));
const AdminSalesAnalysisPage      = lazy(() => import('./pages/sales-analysis/AdminSalesAnalysisPage'));

// -- Notifications sub-pages --------------------------------
const AdminNotificationsPage      = lazy(() => import('./pages/notifications/AdminNotificationsPage'));
const AdminSendEmailPage          = lazy(() => import('./pages/notifications/AdminSendEmailPage'));
const AdminSendAllEmailPage       = lazy(() => import('./pages/notifications/AdminSendAllEmailPage'));
const AdminSendPushPage           = lazy(() => import('./pages/notifications/AdminSendPushPage'));
const AdminSiteNotificationsPage  = lazy(() => import('./pages/notifications/AdminSiteNotificationsPage'));

// -- API Settings sub-pages --------------------------------
const AdminApiSettingsPage        = lazy(() => import('./pages/api-settings/AdminApiSettingsPage'));
const AdminApiLinksPage           = lazy(() => import('./pages/api-settings/AdminApiLinksPage'));
const AdminApiConfigPage          = lazy(() => import('./pages/api-settings/AdminApiConfigPage'));
const AdminAirtimeApiPage         = lazy(() => import('./pages/api-settings/AdminAirtimeApiPage'));
const AdminWalletApiPage          = lazy(() => import('./pages/api-settings/AdminWalletApiPage'));
const AdminDataApiPage            = lazy(() => import('./pages/api-settings/AdminDataApiPage'));
// PalmPay temporarily hidden — see route below. Re-enable both to restore.
// const AdminPalmPayApiPage      = lazy(() => import('./pages/api-settings/AdminPalmPayApiPage'));
const AdminVtpassPage             = lazy(() => import('./pages/api-settings/AdminVtpassPage'));
const AdminApiSlotsPage           = lazy(() => import('./pages/api-settings/AdminApiSlotsPage'));
const AdminDataIdPage             = lazy(() => import('./pages/api-settings/AdminDataIdPage'));
const AdminPocketFiPage           = lazy(() => import('./pages/api-settings/AdminPocketFiPage'));
const AdminSelfApiIntegrationPage = lazy(() => import('./pages/api-settings/AdminSelfApiIntegrationPage'));

// -- Services sub-pages ------------------------------------
const AdminServicesPage     = lazy(() => import('./pages/services/AdminServicesPage'));
const AdminDataPlansPage    = lazy(() => import('./pages/data-plans/AdminDataPlansPage'));
const AdminAirtimePage      = lazy(() => import('./pages/airtime/AdminAirtimePage'));
const AdminCablePage        = lazy(() => import('./pages/cable/AdminCablePage'));
const AdminElectricityPage  = lazy(() => import('./pages/electricity/AdminElectricityPage'));
const AdminExamPage         = lazy(() => import('./pages/exam/AdminExamPage'));
const AdminCashbackPage     = lazy(() => import('./pages/cashback/AdminCashbackPage'));

// -- Other Settings sub-pages ------------------------------
const AdminOtherSettingsPage         = lazy(() => import('./pages/other-settings/AdminOtherSettingsPage'));
const AdminUpgradeRequestsPage       = lazy(() => import('./pages/upgrade-requests/AdminUpgradeRequestsPage'));
const AdminUpgradeRequestDetailsPage = lazy(() => import('./pages/upgrade-requests/AdminUpgradeRequestDetailsPage'));
const AdminAdsBannersPage            = lazy(() => import('./pages/ads-banners/AdminAdsBannersPage'));
const AdminFaqsPage                  = lazy(() => import('./pages/other-settings/AdminFaqsPage'));
const AdminBlacklistPage             = lazy(() => import('./pages/other-settings/AdminBlacklistPage'));
const AdminDataMappingPage           = lazy(() => import('./pages/other-settings/AdminDataMappingPage'));

// -- New pages -----------------------------------------------
const AdminUpdatePage                = lazy(() => import('./pages/update/AdminUpdatePage'));
const AdminCouponPage                = lazy(() => import('./pages/coupon/AdminCouponPage'));
const AdminUserStatsPage             = lazy(() => import('./pages/user-stats/AdminUserStatsPage'));

// -- Spinner ------------------------------------------------
function AdminSpinner() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span className="spinner spinner-dark" style={{ width: 36, height: 36, borderWidth: 3 }} />
    </div>
  );
}

// -- Guards -------------------------------------------------
function AdminPrivateRoute({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <AdminSpinner />;
  return admin ? children : <Navigate to={`${ADMIN_BASE}/login`} replace />;
}

function AdminPublicRoute({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <AdminSpinner />;
  return admin ? <Navigate to={`${ADMIN_BASE}/dashboard`} replace /> : children;
}

// -- Inner shell (needs context) ----------------------------
function AdminShell() {
  return (
    <Suspense fallback={<AdminSpinner />}>
      <Routes>
        {/* Public: login */}
        <Route
          path="login"
          element={
            <AdminPublicRoute>
              <AdminLoginPage />
            </AdminPublicRoute>
          }
        />

        {/* Protected: everything inside AdminLayout */}
        <Route
          element={
            <AdminPrivateRoute>
              <AdminLayout />
            </AdminPrivateRoute>
          }
        >
          <Route index                    element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard"         element={<AdminDashboardPage />} />
          <Route path="subscribers"       element={<AdminSubscribersPage />} />
          <Route path="subscribers/:id"   element={<AdminSubscriberDetailsPage />} />
          <Route path="transactions"      element={<AdminTransactionsPage />} />
          <Route path="transactions/:ref" element={<AdminTransactionDetailsPage />} />
          <Route path="support"           element={<AdminSupportPage />} />
          <Route path="settings"          element={<AdminSettingsPage />} />
          <Route path="system-users"      element={<AdminSystemUsersPage />} />
          <Route path="account"           element={<AdminAccountPage />} />
          <Route path="credit-debit"      element={<AdminCreditDebitPage />} />
          <Route path="credit-debit/:id"  element={<AdminCreditDebitPage />} />
          <Route path="sales-analysis"    element={<AdminSalesAnalysisPage />} />

          {/* Notifications routes */}
          <Route path="notifications">
            <Route index                     element={<AdminNotificationsPage />} />
            <Route path="send-email"         element={<AdminSendEmailPage />} />
            <Route path="send-all-email"     element={<AdminSendAllEmailPage />} />
            <Route path="send-push"          element={<AdminSendPushPage />} />
            <Route path="site-notifications" element={<AdminSiteNotificationsPage />} />
          </Route>

          {/* API Settings routes */}
          <Route path="api-settings">
            <Route index                   element={<AdminApiSettingsPage />} />
            <Route path="api-links"        element={<AdminApiLinksPage />} />
            <Route path="api-config"       element={<AdminApiConfigPage />} />
            <Route path="airtime"          element={<AdminAirtimeApiPage />} />
            <Route path="wallet"           element={<AdminWalletApiPage />} />
            <Route path="data"             element={<AdminDataApiPage />} />
            {/* PalmPay temporarily hidden — uncomment to restore */}
            {/* <Route path="palmpay"       element={<AdminPalmPayApiPage />} /> */}
            <Route path="vtpass"           element={<AdminVtpassPage />} />
            <Route path="api-slots"        element={<AdminApiSlotsPage />} />
            <Route path="data-id"          element={<AdminDataIdPage />} />
            <Route path="pocketfi"         element={<AdminPocketFiPage />} />
            <Route path="self-integration" element={<AdminSelfApiIntegrationPage />} />
          </Route>

          {/* Services routes */}
          <Route path="data-plans" element={<Navigate to={`${ADMIN_BASE}/services/data-plans`} replace />} />
          <Route path="services">
            <Route index              element={<AdminServicesPage />} />
            <Route path="data-plans"  element={<AdminDataPlansPage />} />
            <Route path="airtime"     element={<AdminAirtimePage />} />
            <Route path="cable"       element={<AdminCablePage />} />
            <Route path="electricity" element={<AdminElectricityPage />} />
            <Route path="exam"        element={<AdminExamPage />} />
            <Route path="cashback"    element={<AdminCashbackPage />} />
          </Route>

          {/* Other Settings routes */}
          <Route path="upgrade-requests"     element={<Navigate to={`${ADMIN_BASE}/other-settings/upgrade-requests`} replace />} />
          <Route path="upgrade-requests/:id" element={<Navigate to={`${ADMIN_BASE}/other-settings/upgrade-requests/:id`} replace />} />
          <Route path="ads-banners"          element={<Navigate to={`${ADMIN_BASE}/other-settings/ads-banners`} replace />} />
          <Route path="other-settings">
            <Route index                       element={<AdminOtherSettingsPage />} />
            <Route path="upgrade-requests"     element={<AdminUpgradeRequestsPage />} />
            <Route path="upgrade-requests/:id" element={<AdminUpgradeRequestDetailsPage />} />
            <Route path="ads-banners"          element={<AdminAdsBannersPage />} />
            <Route path="faqs"                 element={<AdminFaqsPage />} />
            <Route path="blacklist"            element={<AdminBlacklistPage />} />
            <Route path="data-mapping"         element={<AdminDataMappingPage />} />
            <Route path="coupon"               element={<AdminCouponPage />} />
          </Route>

          {/* User Stats */}
          <Route path="user-stats" element={<AdminUserStatsPage />} />
          <Route path="update"      element={<AdminUpdatePage />} />
        </Route>

        <Route path="*" element={<Navigate to={`${ADMIN_BASE}/dashboard`} replace />} />
      </Routes>
    </Suspense>
  );
}

export default function AdminRouter() {
  return (
    <AdminAuthProvider>
      <AdminShell />
    </AdminAuthProvider>
  );
}